import * as _ from 'lodash-es';
import { createSelector, lruMemoize } from 'reselect';

import type { RootState as State } from 'vms/app/rootReducer';
import type {
  InspectionTaskResult,
  InspectionCategory,
} from 'vms/features/inspection/types';

export const selectInspectionCategories = (state: State) => state.inspection.categories;
export const selectInspectionCategoriesList = createSelector(
  selectInspectionCategories,
  (categories) => _.sortBy(_.values(categories), 'position')
);
export const selectInspectionTasks = (state: State) => state.inspection.tasks;
const selectInspectionCategory = (state: State, categoryId: string) =>
  state.inspection.categories[categoryId];
export const selectCachedInspectionTasksListByCategory = createSelector(
  [selectInspectionCategory, selectInspectionTasks],
  (category, tasks) => {
    if (!category) {
      return [];
    }
    const categoryTasks = category.tasks.map((taskId) => tasks[taskId]).filter(Boolean);
    return _.sortBy(categoryTasks, 'position');
  },
  { memoize: lruMemoize, memoizeOptions: { maxSize: 50 } }
);

export const selectInspectionTasksList = createSelector(selectInspectionTasks, (tasks) =>
  _.sortBy(_.values(tasks), 'position')
);

export const selectInspectionResults = (state: State) => state.inspection.results;

export const selectInspectionResultsListByVehicle = createSelector(
  selectInspectionResults,
  (results) => {
    return _.groupBy(results, 'vehicle');
  }
);

const selectVehicleById = (state: State, vehicleId: string) =>
  state.vehicles.entities[vehicleId];

export const selectAvailableActiveInspectionResultList = createSelector(
  [
    selectInspectionResults,
    selectInspectionTasks,
    (_state: State, vehicleId: string) => vehicleId,
    selectVehicleById,
  ],
  (results, tasks, vehicleId, vehicle) => {
    if (!vehicle) {
      return [];
    }
    return Object.values(results).filter((res) => {
      const task = tasks[res.task];
      if (!task) {
        return false;
      }
      return (
        res.vehicle === vehicleId &&
        task.active &&
        task.available_in_statuses.includes(vehicle.status)
      );
    });
  }
);

export const selectAvailableActiveInspectionTasksList = createSelector(
  [selectInspectionTasks, selectVehicleById],
  (tasks, vehicle) => {
    if (!vehicle) {
      return [];
    }
    return Object.values(tasks).filter((task) => {
      return task.active && task.available_in_statuses.includes(vehicle.status);
    });
  }
);

const selectInspectionCategoryWithVehicle = (
  state: State,
  _vehicleId: string,
  categoryId: string
): InspectionCategory | undefined => state.inspection.categories[categoryId];

export const selectCachedAvailableActiveInspectionTasksListByCategory = createSelector(
  [selectInspectionCategoryWithVehicle, selectAvailableActiveInspectionTasksList],
  (category, tasksList) => {
    if (!category) {
      return [];
    }
    const tasks = _.keyBy(tasksList, 'id');
    const categoryTasks = category.tasks.map((taskId) => tasks[taskId]).filter(Boolean);
    return _.sortBy(categoryTasks, 'position');
  },
  { memoize: lruMemoize, memoizeOptions: { maxSize: 50 } }
);

export const selectCachedActiveAvailableInspectionResultsByTask = createSelector(
  [
    selectAvailableActiveInspectionResultList,
    (_state: State, vehicleId: string) => vehicleId,
  ],
  (results) => _.keyBy(results, 'task'),
  { memoize: lruMemoize, memoizeOptions: { maxSize: 50 } }
);

const selectInspectionResult = (
  state: State,
  resultId: string
): InspectionTaskResult | undefined => state.inspection.results[resultId];

export const selectCachedIsInspectionHighlighted = createSelector(
  [selectInspectionResult],
  (result): boolean => {
    return Boolean(
      result &&
      (result.inspection_status === 'ATTN' ||
        result.technician_status === 'ATTN' ||
        result.qc_status === 'ATTN' ||
        result.parts.length)
    );
  },
  { memoize: lruMemoize, memoizeOptions: { maxSize: 200 } }
);
